import { useCallback, useContext, useEffect, useState } from "react";
import { PublicContractDetails } from "../../../pages/contract/publicContractDetails";
import { isLoggedIn } from "../authContext";
import { ContractContext } from "../../../providers/contract";
import {
  EContractType,
  IContractDetailsResponseData,
} from "../../api/services/contract/models";
import ComplexContractDetails from "../../../pages/business/complexContractDetails";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  deleteExternalContractById,
  getExternalMilestoneContracts,
} from "../../api/services/contract";
import { ROUTES } from "../../constants";
import MilestoneDetails from "../../../pages/business/milestoneDetails";
import { AuthConfig } from "../config/authConfig";
import {
  buildTwoPartyContractRoute,
  isTwoPartyContract,
} from "../../util/twoPartyContract";
import TwoPartyDetails from "../../../pages/business/twoPartyContract/details";
import { ECONTRACT_STATUS } from "../../../enums";

type ExtendedContract = IContractDetailsResponseData & {
  title: string;
  deadline: string;
};

const ContractRoute = ({ isAdmin }: { isAdmin: boolean }) => {
  const { contractType, contract, actions } = useContext(ContractContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [milestones, setMilestones] = useState<ExtendedContract[]>([]);

  const fetchMilestoneDetails = useCallback(
    async (id: string) => {
      try {
        const response = await getExternalMilestoneContracts({
          complexParentId: id,
          // A two-party project has one transaction, drafts included (activeChildrenCount
          // leaves drafts out).
          pageSize: isTwoPartyContract(contract)
            ? 1
            : contract?.activeChildrenCount,
        });
        setMilestones(
          response?.data?.content?.map((content) => ({
            ...content,
            title: content?.itemDetail?.title,
            deadline: content?.deadLine || "",
          }))
        );
      } catch (error) {
        console.error(error);
      }
    },
    [contract]
  );

  useEffect(() => {
    if (id && contractType === EContractType.COMPLEX_CONTRACT && !isAdmin)
      fetchMilestoneDetails(id);
  }, [contractType, fetchMilestoneDetails, id, isAdmin]);

  // A two-party project is its one transaction (WAF-3235): open that, or resume it in the
  // two-party wizard while it is a draft.
  const twoPartyMilestone =
    contractType === EContractType.COMPLEX_CONTRACT &&
    isTwoPartyContract(contract)
      ? milestones[0]
      : undefined;
  useEffect(() => {
    if (!twoPartyMilestone?.id) return;
    actions.resetContractState();
    navigate(
      twoPartyMilestone.status === ECONTRACT_STATUS.DRAFT
        ? buildTwoPartyContractRoute(id, twoPartyMilestone.id)
        : ROUTES.CONTRACT_DETAILS.replace(":id", twoPartyMilestone.id),
      { replace: true, state: { from: location.state?.from } }
    );
    // Only the milestone decides; the rest is read when it arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoPartyMilestone]);

  if (!contract || twoPartyMilestone) {
    return <div></div>;
  }

  if (contractType === EContractType.COMPLEX_CONTRACT) {
    return authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
      authorities?.includes("CLIENT_ADMIN") ||
      authorities?.includes("ORGANIZATION_OWNER") ? (
      <ComplexContractDetails
        title={contract?.itemDetail?.title || ""}
        description={contract?.itemDetail?.description || ""}
        milestonesArray={milestones}
        totalAmount={contract?.activeChildrenTotalAmount}
        totalMilestones={contract?.activeChildrenCount}
        totalParties={contract?.activeChildrenPartiesCount}
        showBreadCrumbs
        handleAddMilestone={
          isTwoPartyContract(contract)
            ? undefined
            : () =>
                navigate(
                  ROUTES.CREATE_COMPLEX_CONTRACT.replace(
                    ":complexId?/",
                    id || ""
                  ).replace(":milestoneId?", "")
                )
        }
        handleDeleteMilestone={async (index) => {
          await deleteExternalContractById(milestones[index]?.id || "");
          window.location.reload();
        }}
      />
    ) : (
      <></>
    );
  }

  if (
    contractType === EContractType.MILESTONE_CONTRACT &&
    !isAdmin &&
    (authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
      authorities?.includes("CLIENT_ADMIN") ||
      authorities?.includes("ORGANIZATION_OWNER"))
  ) {
    return isTwoPartyContract(contract) ? (
      <TwoPartyDetails />
    ) : (
      <MilestoneDetails />
    );
  }

  // For other contract types, show public view if not logged in
  return !isLoggedIn() ? <PublicContractDetails /> : <></>;
};

export default ContractRoute;
